import { useMemo } from 'react';
import { HCL_V2_FIND_PRODUCT_BY_PARTNUMBER_CAROUSEL } from '@/data/constants/product';
import { useProducts } from '@/data/Content/Products';
import { useProductsWithInfoElectronico } from '@/data/Content/_Products_InfoElectronico';
import type {
	RecommendationItem,
	RecommendationResponse,
	RecommendationSizes,
	RecommendationType,
} from '@/data/recom';
import { ProductType } from '@/data/types/Product';
import type {
	DpIARecommendationItem,
	DpIARecommendationResponse,
} from 'integration/generated/transactions/data-contracts';
import { useGetRecomRecommendation } from 'integration/hooks/useGetRecomRecommendation';
import {
	allOutfitPartNumbers,
	partNumbersIARecommendationExtractorByUnknown,
	selectMostAvailableOutfit,
} from 'integration/utils/partNumbersIARecommendationExtractorByUnknown';

export type DppIACarouselType =
	| 'recommendation'
	| 'outfit'
	| 'complements'
	| 'sameBrand'
	| 'family';

export const DPP_RECOMMENDATION_TYPES: RecommendationType[] = [
	'outfit',
	'complements',
	'sameBrand',
];

const DPP_FAMILY_RECOMMENDATION_TYPES: RecommendationType[] = [
	...DPP_RECOMMENDATION_TYPES,
	'family',
];

const toDpIARecommendationItem = ({
	vectorSource,
	scoreBreakdown,
	...item
}: RecommendationItem): DpIARecommendationItem => ({
	...item,
	vector_source: vectorSource as DpIARecommendationItem['vector_source'],
	score_breakdown: scoreBreakdown,
});

export const toDpIARecommendationResponse = ({
	data,
}: RecommendationResponse): DpIARecommendationResponse => ({
	data: {
		recommendations: (data?.recommendations ?? []).map(toDpIARecommendationItem),
		complements: data?.complements ?? [],
		outfit: [],
	},
});

export const useDppIARecommendation = ({
	partNumber,
	type,
	filters,
	sizes,
	contract,
	orderExtractor,
	seedPartNumbers,
	withoutIdentity,
}: {
	partNumber?: string;
	type: DppIACarouselType;
	filters?: Record<string, unknown>[];
	sizes?: RecommendationSizes;
	contract: string;
	orderExtractor?: (products: ProductType[], data: DpIARecommendationResponse) => ProductType[];
	seedPartNumbers?: string[];
	withoutIdentity?: boolean;
}) => {
	const { data, isLoading: isLoadingRecommendation } = useGetRecomRecommendation({
		partNumber,
		type: type === 'family' ? DPP_FAMILY_RECOMMENDATION_TYPES : DPP_RECOMMENDATION_TYPES,
		filters,
		sizes,
		sizeMode: 'prefer',
		partNumbers: seedPartNumbers,
		withoutIdentity,
	});
	const isOutfit = type === 'outfit';

	const partNumbers = useMemo(() => {
		if (!data) {
			return [];
		}
		return isOutfit
			? allOutfitPartNumbers(data)
			: partNumbersIARecommendationExtractorByUnknown(type, data);
	}, [data, isOutfit, type]);

	const { products: rawProducts, loading: loadingProducts } = useProducts({
		partNumber: partNumbers,
		contractId: contract,
		query: { profileName: HCL_V2_FIND_PRODUCT_BY_PARTNUMBER_CAROUSEL },
	});

	const { data: productsWithInfoElectronico, isLoading: loadingInfoElectronicos } =
		useProductsWithInfoElectronico(rawProducts);

	const products = useMemo(() => {
		if (!data) {
			return productsWithInfoElectronico;
		}
		const selected = isOutfit
			? selectMostAvailableOutfit(productsWithInfoElectronico, data)
			: productsWithInfoElectronico;
		return orderExtractor ? orderExtractor(selected, toDpIARecommendationResponse(data)) : selected;
	}, [data, isOutfit, orderExtractor, productsWithInfoElectronico]);

	return {
		isLoadingRecommendation,
		isLoadingProducts: loadingInfoElectronicos || loadingProducts,
		products,
	};
};
